import { type ReactNode, useMemo, useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { AdminFeatureFlagContext } from "./AdminFeatureFlagContext";
import { useAppContext } from "useAppContext";
import { EnumUserUserType } from "gql/generated";
import { useWorkbenchSession } from "shared/contextProviders/workbenchSession";

const FEATURE_FLAGS_BY_ADMIN_USER = gql`
  query FeatureFlagsByAdminUser {
    featureFlagsByAdminUser {
      _id
      key
      group
      enabled
      graduated
      hasExplicitGate
    }
  }
`;

type ResolvedFlag = {
  _id: string;
  key: string;
  group: string;
  enabled: boolean;
  graduated: boolean;
};

type Props = {
  children: ReactNode;
};

export const AdminFeatureFlagProvider = ({ children }: Props) => {
  const { dbUser } = useAppContext();
  const isTnbInternal = dbUser?.userType === EnumUserUserType.TnbInternal;
  const workbenchFlagKey = useWorkbenchSession().session?.flagKey;

  const { data, loading } = useQuery<{
    featureFlagsByAdminUser: ResolvedFlag[];
  }>(FEATURE_FLAGS_BY_ADMIN_USER, {
    skip: !isTnbInternal,
    fetchPolicy: "cache-and-network",
  });

  const flags = useMemo(() => {
    const map = new Map<string, { enabled: boolean; group: string }>();
    if (data?.featureFlagsByAdminUser) {
      for (const flag of data.featureFlagsByAdminUser) {
        map.set(flag.key, { enabled: flag.enabled, group: flag.group });
      }
    }
    return map;
  }, [data]);

  const isEnabled = useCallback(
    (flagKey: string): boolean => {
      if (workbenchFlagKey === flagKey) return true;
      return flags.get(flagKey)?.enabled ?? false;
    },
    [flags, workbenchFlagKey],
  );

  const value = useMemo(
    () => ({ isEnabled, flags, loading }),
    [isEnabled, flags, loading],
  );

  return (
    <AdminFeatureFlagContext.Provider value={value}>
      {children}
    </AdminFeatureFlagContext.Provider>
  );
};
